import { Controller } from '@hotwired/stimulus'
import * as echarts from 'echarts/core'
import { DatasetComponent, GridComponent, LegendComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent } from 'echarts/components'
import { BarChart, GaugeChart, LineChart, PieChart, SankeyChart, ScatterChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import 'echarts/i18n/langDE.js'
import colors from '@quis-de/tailwindcss-config/src/colors'
import MorphReconnect from '../../javascript/mixins/morph_reconnect'
import {
  numberToCurrency,
  numberToCurrencyPerArea,
  numberToDays, numberToEstates,
  numberToHuman, numberToPercentage
} from '../../javascript/helpers/number_helper'

export { colors }

echarts.use([
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkAreaComponent,
  BarChart,
  GaugeChart,
  LineChart,
  PieChart,
  ScatterChart,
  SankeyChart,
  CanvasRenderer
])

export class BaseChartController extends MorphReconnect(Controller) {
  static values = {
    title: { type: String, default: '' },
    pdf: { type: Boolean, default: false },
    customColors: Array,
    locale: { type: String, default: 'DE' },
    tooltipFormatter: { type: Object, default: {} },
    tooltipValueFormatter: { type: String, default: 'value' },
    legendType: { type: String, default: 'none' },
    series: Array
  }

  colors = (this.customColorsValue && this.customColorsValue.length > 0) ? this.customColorsValue.map(value => colors[value]) : Object.values(colors.chart)

  connect () {
    super.connect()
    this.chart = echarts.init(this.element, null, {
      locale: this.localeValue
    })
    this.chart.setOption(this.options())
    this.chart.on('click', this.handleClick)

    this.bindResize = this.resize.bind(this)
    window.addEventListener('resize', this.bindResize)
  }

  disconnect () {
    window.removeEventListener('resize', this.bindResize)
    this.chart.dispose()
    super.disconnect()
  }

  resize () {
    if (this.chart !== null && this.chart !== undefined) {
      this.chart.resize()
    }
  }

  options () {
    let tooltip

    if (!this.pdfValue) {
      tooltip = this.tooltipOptions()
    }

    return {
      title: this.titleOptions(),
      textStyle: defaultTextStyle,
      color: this.colors,
      animation: false,
      legend: this.legend(),
      tooltip
    }
  }

  titleOptions () {
    return {
      show: this.hasTitleValue,
      text: this.titleValue,
      textStyle: defaultTextStyle
    }
  }

  legend () {
    return this.legendOptions()[this.legendTypeValue]
  }

  legendOptions () {
    return {
      none: {
        show: false
      },
      left: {
        orient: 'vertical',
        top: 'top',
        left: 0,
        itemHeight: 14,
        textStyle: defaultTextStyle,
        tooltip: this.legendTooltip()
      },
      vertical: {
        orient: 'vertical',
        top: 'center',
        right: 0,
        itemHeight: 14,
        textStyle: defaultTextStyle,
        tooltip: this.legendTooltip()
      },
      horizontal: {
        orient: 'horizontal',
        bottom: 0,
        itemHeight: 14,
        textStyle: defaultTextStyle,
        tooltip: this.legendTooltip()
      },
      horizontal_two_lines: {
        orient: 'horizontal',
        bottom: 0,
        itemHeight: 14,
        textStyle: defaultTextStyle,
        tooltip: this.legendTooltip()
      }
    }
  }

  legendTooltip () {
    return {
      show: true,
      formatter: (params) => this.legendTooltipFormatter(params)
    }
  }

  legendTooltipFormatter ({ name }) {
    return this.seriesValue.find(series => {
      return series?.hint && series.title === name
    })?.hint?.replaceAll('\n', '<br/>')
  }

  tooltipOptions () {
    return {
      trigger: 'item',
      valueFormatter: tooltipValueFormatter(this.localeValue, this.tooltipValueFormatterValue),
      formatter: this.tooltipFormatter()
    }
  }

  handleClick (params) {
    if (params.data.href !== undefined) {
      // eslint-disable-next-line no-undef
      Turbo.visit(params.data.href)
    }
  }

  tooltipFormatter () {
    return this.tooltipFormatters()[this.tooltipFormatterValue.formatter || 'default']
  }

  tooltipHeaderFormatter () {
    return this.tooltipHeaderFormatters()[this.tooltipFormatterValue.headerFormatter || 'default']
  }

  tooltipSeriesFormatter (seriesConfig) {
    const type = typeof seriesConfig === 'string' ? seriesConfig : seriesConfig?.type
    return this.tooltipSeriesFormatters()[type || 'default']
  }

  tooltipFormatters () {
    return {
      default: (params) => this.tooltipDefaultFormatter(params)
    }
  }

  tooltipSeriesFormatters () {
    return {
      default: (params) => this.tooltipDefaultSeriesFormatter(params),
      none: (_params) => ''
    }
  }

  tooltipDefaultFormatter (params) {
    const header = this.tooltipHeaderFormatter()(params)
    return `
      <div style="margin: 0px 0 0;line-height:1;">
        ${header}
        <div style="margin: ${header === '' ? '-10px' : '0px'} 0 0;line-height:1;">
          ${params.map((series) => {
            const seriesConfig = this.options().series[series.seriesIndex]?.tooltipFormatter
            return this.tooltipSeriesFormatter(seriesConfig)(series)
          }).join('\n')}
        </div>
        <div style="clear:both"></div>
      </div>
    `
  }

  tooltipDefaultHeaderFormatter (params) {
    return `
      <div style="margin: 0px 0 0;line-height:1;">
        <span style="font-size:14px;color:#666;font-weight:400;line-height:1;">${params[0].axisValueLabel}</span>
        <div style="clear:both"></div>
      </div>
    `
  }

  tooltipHeaderFormatters () {
    return {
      default: (params) => this.tooltipDefaultHeaderFormatter(params),
      none: (_params) => '',
      labeled: (params) => this.tooltipLabeledHeaderFormatter(params)
    }
  }

  tooltipLabeledHeaderFormatter (params) {
    return `
      <div style="margin: 0px 0 0;line-height:1;">
        <span style="font-size:14px;color:#666;font-weight:400;line-height:1;">${this.tooltipFormatterValue.headerLabel || ''} </span><span style="font-size:14px;color:#666;font-weight:900;line-height:1;">${params[0].axisValueLabel}</span>
        <div style="clear:both"></div>
      </div>
    `
  }

  tooltipDefaultSeriesFormatter (series) {
    let value = tooltipSeriesValue(series)
    if (typeof this.options().series[series.seriesIndex].tooltip?.valueFormatter === 'function') {
      value = this.options().series[series.seriesIndex].tooltip.valueFormatter(value)
    } else if (typeof this.tooltipOptions()?.valueFormatter === 'function') {
      value = this.tooltipOptions().valueFormatter(value)
    }
    const name = series.seriesName.replace('-\n', '')

    return `
      <div style="margin: 10px 0 0;line-height:1;">
        ${series.marker}
        <span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">${name}</span>
        <span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">${value}</span>
        <div style="clear:both"></div>
      </div>
    `
  }
}

export const defaultTextStyle = {
  color: colors.neutral[600],
  fontSize: 14,
  fontFamily: 'Lato, sans-serif'
}

export const defaultBoldTextStyle = {
  ...defaultTextStyle,
  fontWeight: 'bold',
  fontSize: 16
}

export function tooltipValueFormatter (locale, type, options = undefined) {
  return {
    estates: (value) => numberToEstates(value, locale),
    value: (value) => numberToHuman(value, locale),
    days: (value) => numberToDays(value, locale),
    currency: (value) => numberToCurrency(value, locale),
    currency_by_area: (value) => numberToCurrencyPerArea(value, locale),
    percentage: (value) => numberToPercentage(value, locale, options)
  }[type]
}

export function tooltipSeriesValue (series) {
  if (typeof series.value === 'number') return series.value

  let value
  switch (series.axisDim) {
    case 'x':
      value = series.value.y
      break
    case 'y':
      value = series.value.x
      break
  }
  return value
}

export default {
  BaseChartController,
  colors,
  defaultTextStyle,
  defaultBoldTextStyle,
  tooltipValueFormatter,
  tooltipSeriesValue
}
